import type { CommunicationDto, DepartmentDto, UserDto } from '@shared/api/generated/model'
import { useSearchGlobal } from '@shared/api/generated/search/search'
import { useDebounce } from '@shared/hooks/useDebounce'
import { cn } from '@shared/lib/utils/cn'
import { Loader2, Search } from 'lucide-react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'

interface GlobalSearchBarProps {
  className?: string
}

export function GlobalSearchBar({ className }: GlobalSearchBarProps) {
  const inputId = useId()
  const listboxId = useId()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const debouncedQuery = useDebounce(query, 300)

  const { data, isFetching, isError, refetch } = useSearchGlobal(
    { q: debouncedQuery },
    {
      query: {
        enabled: debouncedQuery.length >= 2,
      },
    },
  )

  const result = data?.data

  const hasResults =
    (result?.communications?.length ?? 0) > 0 ||
    (result?.users?.length ?? 0) > 0 ||
    (result?.departments?.length ?? 0) > 0

  const showDropdown = isOpen && debouncedQuery.length >= 2

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleClose()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
        inputRef.current?.blur()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleClose])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setIsOpen(true)
  }

  const handleItemClick = () => {
    setQuery('')
    setIsOpen(false)
  }

  const communications = result?.communications?.slice(0, 5) ?? []
  const users = result?.users?.slice(0, 5) ?? []
  const departments = result?.departments?.slice(0, 5) ?? []
  const searchItems = useMemo(
    () => [
      ...communications.map((item) => ({
        id: `communication-${item.id}`,
        label: item.title ?? 'Comunicado',
        description: undefined,
        to: `/communications/${item.id}`,
      })),
      ...users.map((item) => ({
        id: `user-${item.id}`,
        label: item.name ?? 'Usuário',
        description: item.email ?? undefined,
        to: `/users/${item.id}/edit`,
      })),
      ...departments.map((item) => ({
        id: `department-${item.id}`,
        label: item.name ?? 'Departamento',
        description: undefined,
        to: `/departments/${item.id}`,
      })),
    ],
    [communications, users, departments],
  )

  useEffect(() => {
    setActiveIndex(searchItems.length > 0 ? 0 : -1)
  }, [searchItems])

  return (
    <div ref={containerRef} className={cn('relative isolate w-full max-w-md', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && searchItems.length === 0 && query.length >= 2) {
              event.preventDefault()
              navigate(`/search?q=${encodeURIComponent(query)}`)
              handleItemClick()
              return
            }
            if (!showDropdown || searchItems.length === 0) return

            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setActiveIndex((prev) => (prev + 1) % searchItems.length)
            }

            if (event.key === 'ArrowUp') {
              event.preventDefault()
              setActiveIndex((prev) => (prev <= 0 ? searchItems.length - 1 : prev - 1))
            }

            if (event.key === 'Enter' && activeIndex >= 0) {
              event.preventDefault()
              const selected = searchItems[activeIndex]
              if (selected) {
                navigate(selected.to)
                handleItemClick()
              }
            }
          }}
          placeholder="Buscar comunicados, usuários, departamentos..."
          className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          role="combobox"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={activeIndex >= 0 ? searchItems[activeIndex]?.id : undefined}
        />
      </div>

      {showDropdown && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Resultados da busca global"
          className="absolute top-full left-0 right-0 z-50 mt-2 overflow-hidden rounded-xl border border-border/80 bg-card text-popover-foreground shadow-xl shadow-black/15 backdrop-blur supports-[backdrop-filter]:bg-card/95"
        >
          {isFetching && (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {isError && (
            <div role="alert" className="p-4 text-sm">
              Falha na busca.{' '}
              <button type="button" onClick={() => refetch()}>
                Tentar novamente
              </button>
            </div>
          )}
          {!isError && !isFetching && !hasResults && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Nenhum resultado encontrado
            </div>
          )}

          {!isError && !isFetching && hasResults && (
            <div className="max-h-96 overflow-y-auto py-2">
              {communications.length > 0 && (
                <section>
                  <p className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Comunicados
                  </p>
                  {communications.map((item: CommunicationDto) => (
                    <Link
                      id={`communication-${item.id}`}
                      key={item.id}
                      to={`/communications/${item.id}`}
                      onClick={handleItemClick}
                      role="option"
                      aria-selected={searchItems[activeIndex]?.id === `communication-${item.id}`}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                        searchItems[activeIndex]?.id === `communication-${item.id}` &&
                          'bg-accent text-accent-foreground',
                      )}
                    >
                      <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{item.title}</span>
                    </Link>
                  ))}
                </section>
              )}

              {users.length > 0 && (
                <section>
                  <p className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Usuários
                  </p>
                  {users.map((item: UserDto) => (
                    <Link
                      id={`user-${item.id}`}
                      key={item.id}
                      to={`/users/${item.id}/edit`}
                      onClick={handleItemClick}
                      role="option"
                      aria-selected={searchItems[activeIndex]?.id === `user-${item.id}`}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                        searchItems[activeIndex]?.id === `user-${item.id}` &&
                          'bg-accent text-accent-foreground',
                      )}
                    >
                      <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{item.name}</span>
                      {item.email && (
                        <span className="truncate text-xs text-muted-foreground">{item.email}</span>
                      )}
                    </Link>
                  ))}
                </section>
              )}

              {departments.length > 0 && (
                <section>
                  <p className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Departamentos
                  </p>
                  {departments.map((item: DepartmentDto) => (
                    <Link
                      id={`department-${item.id}`}
                      key={item.id}
                      to={`/departments/${item.id}`}
                      onClick={handleItemClick}
                      role="option"
                      aria-selected={searchItems[activeIndex]?.id === `department-${item.id}`}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                        searchItems[activeIndex]?.id === `department-${item.id}` &&
                          'bg-accent text-accent-foreground',
                      )}
                    >
                      <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  ))}
                </section>
              )}
            </div>
          )}
          <Link
            to={`/search?q=${encodeURIComponent(debouncedQuery)}`}
            onClick={handleItemClick}
            className="block p-3 text-sm text-primary"
          >
            Ver todos os resultados
          </Link>
        </div>
      )}
    </div>
  )
}
